import React from 'react';
import { Progress } from '@/components/ui/progress';
import { DailyNutrients } from '@/types/meal-plan';
import { roundNutritionDisplay } from '@/utils/nutrition-display-format';

interface NutrientTrackerProps {
  nutrients: DailyNutrients;
  targets?: {
    calories?: number;
    protein?: number;
    carbs?: number;
    fat?: number;
    vitaminA?: number;
    vitaminC?: number;
    calcium?: number;
    iron?: number;
  };
}


const NutrientTracker: React.FC<NutrientTrackerProps> = ({ nutrients, targets }) => {
  // Safety check - if targets is not provided, don't render
  if (!targets) {
    return (
      <div className="space-y-4">
        <div className="text-center text-gray-500 py-4">
          Loading nutrition targets...
        </div>
      </div>
    );
  }
  
  // Calculate percentages of DRI met (progress bars use full precision; labels use custom recipe display rules)
  const calculatePercentage = (value: number, target: number) => {
    if (!target || target <= 0 || !Number.isFinite(value)) return 0;
    return Math.min(100, Math.max(0, (value / target) * 100));
  };
  
  // Function to determine progress bar color based on percentage
  const getProgressColor = (percentage: number) => {
    if (percentage < 30) return "bg-red-500";
    if (percentage < 70) return "bg-yellow-500";
    return "bg-green-500";
  };

 

  return (
    <div className="space-y-4">
      <div>
        <div className="flex justify-between items-center mb-1">
          <span className="text-sm font-medium">Calories</span>
          <span className="text-sm text-gray-500">{roundNutritionDisplay(nutrients.totalCalories)} / {roundNutritionDisplay(targets.calories)} kcal</span>
        </div>
        <Progress 
          value={calculatePercentage(nutrients.totalCalories, targets.calories)} 
          className="h-2" 
          indicatorClassName={getProgressColor(calculatePercentage(nutrients.totalCalories, targets.calories))}
        />
      </div>
      
      <div>
        <div className="flex justify-between items-center mb-1">
          <span className="text-sm font-medium">Protein</span>
          <span className="text-sm text-gray-500">{roundNutritionDisplay(nutrients.totalProtein)}g / {roundNutritionDisplay(targets.protein)}g</span>
        </div>
        <Progress 
          value={calculatePercentage(nutrients.totalProtein, targets.protein)} 
          className="h-2" 
          indicatorClassName={getProgressColor(calculatePercentage(nutrients.totalProtein, targets.protein))}
        />
      </div>
      
      <div>
        <div className="flex justify-between items-center mb-1">
          <span className="text-sm font-medium">Carbohydrates</span>
          <span className="text-sm text-gray-500">{roundNutritionDisplay(nutrients.totalCarbs)}g / {roundNutritionDisplay(targets.carbs)}g</span>
        </div>
        <Progress 
          value={calculatePercentage(nutrients.totalCarbs, targets.carbs)} 
          className="h-2" 
          indicatorClassName={getProgressColor(calculatePercentage(nutrients.totalCarbs, targets.carbs))}
        />
      </div>
      
      <div>
        <div className="flex justify-between items-center mb-1">
          <span className="text-sm font-medium">Fat</span>
          <span className="text-sm text-gray-500">{roundNutritionDisplay(nutrients.totalFat)}g / {roundNutritionDisplay(targets.fat)}g</span>
        </div>
        <Progress 
          value={calculatePercentage(nutrients.totalFat, targets.fat)} 
          className="h-2" 
          indicatorClassName={getProgressColor(calculatePercentage(nutrients.totalFat, targets.fat))}
        />
      </div>
      
      <div className="pt-2 border-t border-gray-200">
        <h4 className="text-sm font-medium mb-3">Micronutrients</h4>
        <div className="space-y-3">
          <div>
            <div className="flex justify-between items-center mb-1">
              <span className="text-xs">Vitamin A</span>
              <span className="text-xs text-gray-500">{roundNutritionDisplay(nutrients.micronutrients.vitaminA)}μg / {roundNutritionDisplay(targets.vitaminA)}μg</span>
            </div>
            <Progress 
              value={calculatePercentage(nutrients.micronutrients.vitaminA, targets.vitaminA)} 
              className="h-1.5" 
              indicatorClassName={getProgressColor(calculatePercentage(nutrients.micronutrients.vitaminA, targets.vitaminA))}
            />
          </div>
          
          <div>
            <div className="flex justify-between items-center mb-1">
              <span className="text-xs">Vitamin C</span>
              <span className="text-xs text-gray-500">{roundNutritionDisplay(nutrients.micronutrients.vitaminC)}mg / {roundNutritionDisplay(targets.vitaminC)}mg</span>
            </div>
            <Progress 
              value={calculatePercentage(nutrients.micronutrients.vitaminC, targets.vitaminC)} 
              className="h-1.5" 
              indicatorClassName={getProgressColor(calculatePercentage(nutrients.micronutrients.vitaminC, targets.vitaminC))}
            />
          </div>
          
          <div>
            <div className="flex justify-between items-center mb-1">
              <span className="text-xs">Calcium</span>
              <span className="text-xs text-gray-500">{roundNutritionDisplay(nutrients.micronutrients.calcium)}mg / {roundNutritionDisplay(targets.calcium)}mg</span>
            </div>
            <Progress 
              value={calculatePercentage(nutrients.micronutrients.calcium, targets.calcium)} 
              className="h-1.5" 
              indicatorClassName={getProgressColor(calculatePercentage(nutrients.micronutrients.calcium, targets.calcium))}
            />
          </div>
          
          <div>
            <div className="flex justify-between items-center mb-1">
              <span className="text-xs">Iron</span>
              <span className="text-xs text-gray-500">{roundNutritionDisplay(nutrients.micronutrients.iron)}mg / {roundNutritionDisplay(targets.iron)}mg</span>
            </div>
            <Progress 
              value={calculatePercentage(nutrients.micronutrients.iron, targets.iron)} 
              className="h-1.5" 
              indicatorClassName={getProgressColor(calculatePercentage(nutrients.micronutrients.iron, targets.iron))}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default NutrientTracker;
